import type { OutreachKpis } from '@discovr/types';
import { KpiCard } from '../../../components/common/KpiCard';
import { CheckCircle, EnvelopeSimple, LockSimple, UsersThree, type Icon } from '../../../design-system/icons';
import { formatRate } from './crmFormat';
import { isSigningPending } from './crmOverviewModel';

const UNKNOWN = '—';
export const SIGNING_PENDING_HINT = 'Available once signing is live';

type TileKpis = Pick<OutreachKpis, 'creatorsInPlay' | 'replyRate' | 'ratesLockedNow' | 'contactToSigned' | 'funnelSteps' | 'deliverability'>;

interface TileSpec {
  label: string;
  icon: Icon;
  value: (kpis: TileKpis) => string;
  hint?: (kpis: TileKpis) => string | undefined;
}

const countText = (value: number | null | undefined): string => (value == null ? UNKNOWN : String(value));

/** Go sends 0, not null, when nothing was delivered (no first mail sent, or every one bounced); that reads as "not measured", not 0%. */
function replyRateText(kpis: TileKpis): string {
  const contacted = kpis.funnelSteps?.contacted;
  const delivered = contacted == null ? null : contacted - (kpis.deliverability?.bounced ?? 0);
  return delivered !== null && delivered <= 0 ? UNKNOWN : formatRate(kpis.replyRate);
}

function contactToSignedHint(kpis: TileKpis): string | undefined {
  const unmeasured = (kpis.contactToSigned ?? null) === null;
  return unmeasured && isSigningPending(kpis) ? SIGNING_PENDING_HINT : undefined;
}

/** Figma's four headline tiles. The campaign-scoped CRM has no "Active campaigns" tile (owner, 2026-10-14). */
const TILES: readonly TileSpec[] = [
  { label: 'Creators in play', icon: UsersThree, value: (kpis) => countText(kpis.creatorsInPlay) },
  { label: 'Reply rate', icon: EnvelopeSimple, value: replyRateText },
  { label: 'Rates locked', icon: LockSimple, value: (kpis) => countText(kpis.ratesLockedNow) },
  { label: 'Contact to signed', icon: CheckCircle, value: (kpis) => formatRate(kpis.contactToSigned), hint: contactToSignedHint },
];

/** Tiles held back until the close step exists; empty this list to show them again. */
export const HIDDEN_KPI_TILES: readonly string[] = ['Contact to signed'];

const VISIBLE_TILES = TILES.filter((tile) => !HIDDEN_KPI_TILES.includes(tile.label));

const TILE_ROW = VISIBLE_TILES.length === 4 ? 'grid grid-cols-2 gap-3 lg:grid-cols-4' : 'grid grid-cols-1 gap-3 sm:grid-cols-3';

export function CrmKpiTiles({ kpis }: { kpis: TileKpis }) {
  return (
    <div className={TILE_ROW}>
      {VISIBLE_TILES.map((tile) => (
        <KpiCard key={tile.label} icon={tile.icon} label={tile.label} value={tile.value(kpis)} hint={tile.hint?.(kpis)} />
      ))}
    </div>
  );
}

export function CrmKpiTilesSkeleton() {
  return (
    <div className={TILE_ROW}>
      {VISIBLE_TILES.map((tile) => (
        <KpiCard key={tile.label} icon={tile.icon} label={tile.label} value="" loading />
      ))}
    </div>
  );
}
